/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import HTMLParser from 'html-react-parser';
import { parse } from 'marked';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';

type QnaMakerQnaPairAnswerSectionProps = {
    answer: string;
    isRichTextEnabled: boolean;
    handleSubmitEditQnaPair: (newData: Partial<QnaMakerQnaSwagger>) => void;
    handleEditQna: (buttonPlace: string, section: string, action: string) => void;
};

const messages = defineMessages({
    answer: {
        id: 'qnaMaker.qnaMakerQnaPairAnswerSection.answer',
        defaultMessage: 'Answer'
    },
    editAnswer: {
        id: 'qnaMaker.qnaMakerQnaPairAnswerSection.editAnswer',
        defaultMessage: 'Edit answer'
    },
    stopEditing: {
        id: 'qnaMaker.qnaMakerQnaPairAnswerSection.stopEditing',
        defaultMessage: 'Exit edit mode'
    },
    textPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaPairAnswerSection.textPlaceholder',
        defaultMessage: 'Add answer and press enter'
    },
    collapse: {
        id: 'qnaMaker.qnaMakerQnaPairAnswerSection.collapse',
        defaultMessage: 'collapse Answer button '
    },
    expand: {
        id: 'qnaMaker.qnaMakerQnaPairAnswerSection.expand',
        defaultMessage: 'expand Answer button '
    }
});

const theme = getScopedTheme('QnaMakerQnaPairDetails');

const editButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    label: { color: theme.buttonColor },
    labelDisabled: { color: theme.disabledLabelColor }
});

export const QnaMakerQnaPairAnswerSection = observer((props: QnaMakerQnaPairAnswerSectionProps) => {
    const { answer, isRichTextEnabled, handleEditQna, handleSubmitEditQnaPair } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { increaseNumberOfClicks } = useQnaMetricActions();

    const { language, languageDirection } = rootStore.currentQnaMakerProjectStore;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const [isAnswerExpanded, setIsAnswerExpanded] = React.useState(true);
    const [isEditMode, setIsEditMode] = React.useState(false);
    const [editableAnswer, setEditableAnswer] = React.useState(answer);
    React.useEffect(() => setEditableAnswer(answer), [answer]);

    const handleOnEditAnswer = () => {
        setIsEditMode(true);
        increaseNumberOfClicks();
        handleEditQna('EditAnswer', 'Button', 'Edit');
    };

    const handleStopEditing = () => {
        setIsEditMode(false);
        increaseNumberOfClicks();
        handleEditQna('StopEditingAnswer', 'Button', 'Stop editing');
    };

    const handleTags = (data: string) => {
        if (data) {
            data = data.split('<figure class="image">').join('');
            data = data.split('</figure>').join('');

            data = data.replace(/</g, '&lt;');
            data = data.replace(/>/g, '&gt;');
        }

        return data;
    };

    const markdownTextToHtmlElements = (text: string) => {
        const html = parse(handleTags(text));

        return HTMLParser(html);
    };

    const handleRichEditorOnChange = (_: any, editor: any) => {
        const data = editor.getData();
        setEditableAnswer(data);
    };

    const onReadOnlyAnswerClick = () => {
        if (canWrite) {
            setIsEditMode(true);
            handleEditQna('EditAnswer', 'ClickToEdit', 'Edit');
        }
    };

    const getReadOnlyAnswer = () => (
        <Stack onClick={onReadOnlyAnswerClick}>
            {isRichTextEnabled ? (
                <Stack.Item dir={languageDirection} className="ck" styles={{ root: { whiteSpace: 'break-spaces', padding: '0 5px' } }}>
                    {answer && markdownTextToHtmlElements(answer)}
                </Stack.Item>
            ) : (
                <LuisText className="ck" dir={languageDirection} shouldWrapText>
                    {answer}
                </LuisText>
            )}
        </Stack>
    );

    const handleOnKeyUp = (e: React.KeyboardEvent<HTMLElement | HTMLTextAreaElement>) => {
        if (e.key === LuisKeyCodes.Enter && !e.shiftKey) {
            e.stopPropagation();
            e.preventDefault();
            if (answer !== editableAnswer) {
                handleSubmitEditQnaPair({ answer: editableAnswer });
            }
        }

        if (e.key === LuisKeyCodes.Escape || e.key === LuisKeyCodes.Tab) {
            e.stopPropagation();
            setEditableAnswer(answer);
            handleOnBlur();
        }
    };

    const handleOnBlur = () => {
        setIsEditMode(false);
        if (answer !== editableAnswer) {
            handleSubmitEditQnaPair({ answer: editableAnswer });
        }
    };

    const handleRichTextOnBlur = () => {
        if (answer !== editableAnswer) {
            handleSubmitEditQnaPair({ answer: editableAnswer });
        }
    };

    const getEditableAnswer = () => (
        <>
            {isRichTextEnabled ? (
                <Stack.Item onKeyUp={handleOnKeyUp}>
                    <CKEditor
                        config={{ language }}
                        data={editableAnswer}
                        editor={ClassicEditor}
                        onBlur={handleRichTextOnBlur}
                        onChange={handleRichEditorOnChange}
                        placeholder={localize(messages.textPlaceholder)}
                    />
                </Stack.Item>
            ) : (
                <LuisTextField
                    required
                    multiline
                    autoFocus
                    autoAdjustHeight
                    onBlur={handleOnBlur}
                    value={editableAnswer}
                    dir={languageDirection}
                    onKeyDown={handleOnKeyUp}
                    onChange={(_, v) => setEditableAnswer(v)}
                    placeholder={localize(messages.textPlaceholder)}
                    styles={{ field: { resize: 'vertical !important' } }}
                />
            )}
        </>
    );

    return (
        <Stack role="button" tokens={{ childrenGap: 10 }}>
            <Stack horizontal tokens={{ childrenGap: 5 }}>
                <FontIcon
                    tabIndex={0}
                    aria-hidden={false}
                    aria-label={isAnswerExpanded ? localize(messages.collapse) : localize(messages.expand)}
                    onClick={() => setIsAnswerExpanded(!isAnswerExpanded)}
                    onKeyDown={e => {
                        if (e.key === LuisKeyCodes.Enter) {
                            setIsAnswerExpanded(!isAnswerExpanded);
                        }
                    }}
                    iconName={isAnswerExpanded ? 'ChevronDown' : 'ChevronRight'}
                    style={{ cursor: 'pointer' }}
                />
                <FontIcon iconName="ContextMenu" />
                <LuisText fontWeight={600}>{localize(messages.answer)}</LuisText>
            </Stack>
            {isAnswerExpanded && (
                <Stack tokens={{ childrenGap: 5 }}>
                    <RBACTooltip
                        hidden={canWrite}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="editQuestionPairAnswerButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            isEditMode && isRichTextEnabled ? (
                                <ActionButton
                                    styles={editButtonStyles}
                                    onClick={handleStopEditing}
                                    iconProps={{ iconName: 'Cancel' }}
                                    text={localize(messages.stopEditing)}
                                    disabled={!canWrite}
                                />
                            ) : (
                                <ActionButton
                                    styles={editButtonStyles}
                                    onClick={handleOnEditAnswer}
                                    iconProps={{ iconName: 'Edit' }}
                                    text={localize(messages.editAnswer)}
                                    disabled={!canWrite || isEditMode}
                                />
                            )
                        }
                    />
                    {isEditMode ? getEditableAnswer() : getReadOnlyAnswer()}
                </Stack>
            )}
        </Stack>
    );
});
